import {
  useQuery,
  UseQueryOptions,
  useMutation,
  UseMutationOptions,
  useQueryClient,
} from "@tanstack/react-query";
import { useContext } from "react";
import { AppContext } from "../context/app";
import {
  getUserProfileByUUID,
  GetMemberResult,
  QuickAction,
  getQuickActions,
  getQuickActionsList,
  toggleQuickAction,
  ToggleQuickActionParams,
  updateQuickActionPositions,
  UpdateQuickActionPositionsParams,
  resetQuickActions,
  QuickActionBatchUpdateResponse,
  PinnedDateSettings,
  pinDateSettings,
  getPinnedDateSettings,
} from "./userApi";

export function useProfileQuery(
  uuid: string,
  options?: UseQueryOptions<GetMemberResult>,
) {
  const app = useContext(AppContext);

  return useQuery({
    ...options,
    queryKey: ["member-profile", uuid],
    queryFn: () => getUserProfileByUUID(uuid, app.client.uuid),
  });
}

export function useQuickActionsQuery(options?: UseQueryOptions<QuickAction[]>) {
  return useQuery<QuickAction[]>({
    ...options,
    queryKey: ["quick-actions"],
    queryFn: () => getQuickActions(),
  });
}

export function useQuickActionsListQuery(
  options?: UseQueryOptions<QuickAction[]>,
) {
  return useQuery<QuickAction[]>({
    ...options,
    queryKey: ["quick-actions-list"],
    queryFn: () => getQuickActionsList(),
  });
}

export function useToggleQuickActionMutation(
  options?: UseMutationOptions<
    { data: string },
    unknown,
    ToggleQuickActionParams
  >,
) {
  const queryClient = useQueryClient();

  return useMutation<{ data: string }, unknown, ToggleQuickActionParams>({
    ...options,
    mutationFn: (params) => toggleQuickAction(params),
    onSuccess: (data, variables, context) => {
      // Invalidate the quick-actions queries to refetch data
      queryClient.invalidateQueries({ queryKey: ["quick-actions"] });
      queryClient.invalidateQueries({ queryKey: ["quick-actions-list"] });

      // Call the user-provided onSuccess if it exists
      if (options?.onSuccess) {
        options.onSuccess(data, variables, context);
      }
    },
  });
}

export function useUpdateQuickActionPositionsMutation(
  options?: UseMutationOptions<
    QuickActionBatchUpdateResponse,
    unknown,
    UpdateQuickActionPositionsParams
  >,
) {
  const queryClient = useQueryClient();

  return useMutation<
    QuickActionBatchUpdateResponse,
    unknown,
    UpdateQuickActionPositionsParams
  >({
    ...options,
    mutationFn: (params) => updateQuickActionPositions(params),
    onSuccess: (data, variables, context) => {
      // Invalidate the quick-actions queries to refetch data
      queryClient.invalidateQueries({ queryKey: ["quick-actions"] });
      queryClient.invalidateQueries({ queryKey: ["quick-actions-list"] });

      // Call the user-provided onSuccess if it exists
      if (options?.onSuccess) {
        options.onSuccess(data, variables, context);
      }
    },
  });
}

export function useResetQuickActionsMutation(
  options?: UseMutationOptions<{ data: string }, unknown>,
) {
  const queryClient = useQueryClient();
  return useMutation<{ data: string }, unknown>({
    ...options,
    mutationFn: () => resetQuickActions(),
    onSuccess: () => {
      // Invalidate the quick-actions queries to refetch data
      queryClient.invalidateQueries({ queryKey: ["quick-actions"] });
      queryClient.invalidateQueries({ queryKey: ["quick-actions-list"] });
    },
  });
}

export function usePinnedDateSettingsQuery(
  options?: UseQueryOptions<PinnedDateSettings>,
) {
  return useQuery<PinnedDateSettings>({
    ...options,
    queryKey: ["pinned-date-settings"],
    queryFn: () => getPinnedDateSettings(),
  });
}

export function usePinDateSettingsMutation(
  options?: UseMutationOptions<void, unknown, PinnedDateSettings>,
) {
  const queryClient = useQueryClient();

  return useMutation<void, unknown, PinnedDateSettings>({
    ...options,
    mutationFn: (settings) => pinDateSettings(settings),
    onSuccess: (data, variables, context) => {
      // Invalidate the pinned date settings query to refetch data
      queryClient.invalidateQueries({ queryKey: ["pinned-date-settings"] });

      // Call the user-provided onSuccess if it exists
      if (options?.onSuccess) {
        options.onSuccess(data, variables, context);
      }
    },
  });
}
